import React, { useCallback, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';
import { Separator } from 'components/ui/separator';

interface ITagSelectorProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  inputTestId?: string;
  listTestId?: string;
}

/**
 * Shared tag selector with add-on-enter and badge removal.
 *
 * @example
 * <TagSelector
 *   tags={values.tags}
 *   onTagsChange={(newTags) => setFieldValue('tags', newTags)}
 *   inputTestId="bundleTagsInput"
 * />
 */
export const TagSelector: React.FC<ITagSelectorProps> = ({
  tags,
  onTagsChange,
  inputTestId,
  listTestId
}): React.ReactElement => {
  const [inputValue, setInputValue] = useState('');
  const [tagError, setTagError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleAdd = useCallback(
    (value: string): void => {
      if (tags.includes(value)) {
        setTagError("You can't add the same tag twice");
        return;
      }

      const newTags = [...tags, value];
      onTagsChange(newTags);
      setTagError('');
      setInputValue('');
      inputRef.current?.blur();
    },
    [tags, onTagsChange]
  );

  const handleRemove = useCallback(
    (value: string): void => {
      const newTags = tags.filter((t) => t !== value);
      onTagsChange(newTags);
    },
    [tags, onTagsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        handleAdd(inputValue.trim());
      }
    },
    [inputValue, handleAdd]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setInputValue(e.target.value);
      setTagError(
        tags.includes(e.target.value)
          ? "You can't add the same tag twice"
          : ''
      );
    },
    [tags]
  );

  return (
    <>
      <Separator className="my-5 bg-base-border" />
      <RichText
        title="Tags"
        description="Create or add tags to your offer"
        className="mt-4 mb-5"
      />
      <div className="flex flex-col gap-5 mb-9">
        <div>
          <Typography className="text-black font-medium text-sm mb-2">
            Tag Name
          </Typography>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder="Search Tag"
            dataTestId={inputTestId}
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-magnifying-glass"
                className="w-4 h-4"
              />
            }
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            state={tagError ? 'error' : 'default'}
            description={tagError || ''}
          />
          <Typography className="text-zinc-500 text-sm mt-2">
            Press 'Enter' to add a new tag
          </Typography>
          {!!tags.length && (
            <div
              className="mt-3 flex flex-row flex-wrap gap-2"
              data-testid={listTestId}
            >
              {tags.map((val: string) => (
                <Badge
                  key={val}
                  isRemovable
                  onRemove={() => handleRemove(val)}
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
